import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { cloneVoice } from "../../services/creativeService";

const CONSENT_TEXT =
  "Je confirme être le locuteur de cet échantillon et j'autorise OneBotAds à créer une copie synthétique de ma voix pour l'utiliser dans mes propres publicités.";

// Encodage envoyé au backend selon le mimeType réellement choisi par le navigateur —
// MediaRecorder ne supporte pas WAV/LINEAR16 nativement, on capture donc en Opus
// (webm ou ogg selon le navigateur) et on le déclare tel quel à Google Cloud TTS.
// Mapping best-effort, PAS vérifié en direct (l'accès Google est sur liste blanche —
// voir googleVoiceCloneProvider.js) : à confirmer une fois l'accès obtenu.
const pickMimeType = () => {
  const candidates = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/ogg"];
  for (const c of candidates) {
    if (window.MediaRecorder?.isTypeSupported?.(c)) return c;
  }
  return "";
};
const encodingFor = (mimeType) => (mimeType.includes("ogg") ? "OGG_OPUS" : "WEBM_OPUS");

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

// Petit enregistreur réutilisable pour un des deux échantillons (référence / consentement).
const useRecorder = () => {
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = useCallback(async () => {
    setBlob(null);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = pickMimeType();
    const rec = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      setBlob(new Blob(chunksRef.current, { type: mimeType || "audio/webm" }));
    };
    rec.start();
    mediaRef.current = rec;
    setRecording(true);
    setSeconds(0);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  }, []);

  const stop = useCallback(() => {
    mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  return { recording, blob, seconds, start, stop, reset: () => setBlob(null) };
};

// `onCloned({id, label})` — appelé après un clonage réussi, pour sélectionner la
// nouvelle voix immédiatement dans le composeur.
const VoiceCloneRecorder = ({ onCloned, onClose }) => {
  const [label, setLabel] = useState("");
  const [step, setStep] = useState(1); // 1 = échantillon, 2 = consentement
  const [saving, setSaving] = useState(false);
  const reference = useRecorder();
  const consent = useRecorder();

  const canRecord = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  const submit = async () => {
    if (!label.trim()) return toast.warn("Donnez un nom à cette voix.");
    if (!reference.blob || !consent.blob) return toast.warn("Enregistrez les deux échantillons audio.");
    setSaving(true);
    try {
      const mimeType = reference.blob.type || "audio/webm";
      const [referenceAudioBase64, consentAudioBase64] = await Promise.all([
        blobToBase64(reference.blob), blobToBase64(consent.blob),
      ]);
      const res = await cloneVoice({
        label: label.trim(), referenceAudioBase64, consentAudioBase64,
        encoding: encodingFor(mimeType),
      });
      toast.success(`Voix « ${res.label} » clonée.`);
      onCloned?.(res);
    } catch (err) {
      // Le backend explique clairement le blocage réel (liste blanche Google /
      // jeton OAuth2 manquant) plutôt qu'une erreur générique — on l'affiche telle quelle.
      toast.error(err?.response?.data?.message || "Clonage impossible.", { autoClose: 12000 });
    } finally { setSaving(false); }
  };

  if (!canRecord) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
        L'enregistrement audio n'est pas disponible dans ce navigateur (ou hors HTTPS).
      </div>
    );
  }

  const RecorderRow = ({ rec, hint }) => (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={rec.recording ? rec.stop : rec.start}
        className={`px-4 py-2 rounded-xl text-sm font-semibold ${rec.recording ? "bg-red-600 text-white" : "bg-[#221C51] text-white"}`}
      >
        {rec.recording ? `⏹ Arrêter (${rec.seconds}s)` : rec.blob ? "🔁 Réenregistrer" : "🎙 Enregistrer"}
      </button>
      {rec.blob && !rec.recording && (
        <audio controls src={URL.createObjectURL(rec.blob)} className="h-8" />
      )}
      {!rec.blob && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );

  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-[#221C51]">Cloner votre voix</p>
        {onClose && <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">Fermer</button>}
      </div>
      <p className="text-xs text-gray-500">
        Fonctionnalité Google Cloud (Chirp 3 Instant Custom Voice) — l'accès est actuellement
        réservé aux comptes validés par Google. Vous pouvez enregistrer vos échantillons ;
        si l'accès n'est pas encore activé, le message d'erreur l'indiquera clairement.
      </p>

      <input
        value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nom de cette voix (ex: Ma voix)"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]"
      />

      <div className="flex gap-2 text-xs font-semibold">
        <button onClick={() => setStep(1)} className={step === 1 ? "text-[#221C51]" : "text-gray-400"}>1. Échantillon</button>
        <span className="text-gray-300">→</span>
        <button onClick={() => setStep(2)} className={step === 2 ? "text-[#221C51]" : "text-gray-400"}>2. Consentement</button>
      </div>

      {step === 1 && (
        <div>
          <p className="text-xs text-gray-500 mb-2">Lisez librement à voix haute pendant 10 à 15 secondes (voix claire, pas de bruit de fond).</p>
          <RecorderRow rec={reference} hint="Aucun échantillon enregistré." />
        </div>
      )}
      {step === 2 && (
        <div>
          <p className="text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-3 mb-2 italic">« {CONSENT_TEXT} »</p>
          <RecorderRow rec={consent} hint="Lisez la phrase ci-dessus à voix haute." />
        </div>
      )}

      <button
        onClick={submit}
        disabled={saving || !reference.blob || !consent.blob || !label.trim()}
        className="px-5 py-2 bg-[#221C51] text-white text-sm font-semibold rounded-xl disabled:opacity-40"
      >
        {saving ? "Envoi…" : "Cloner cette voix"}
      </button>
    </div>
  );
};

export default VoiceCloneRecorder;
